import type { PractitionerType, TaxonomyOption } from "cmw-client";
import { deltaText, displayAbout } from "@/components/input";
import { suburbOf } from "./address";
import { formatDistance } from "./labels";
import type { ProfessionEntry } from "./profession-list";
import { isAdminSource, isCredentialVerified, type PractitionerCardSource } from "./source";

export type CardContent = {
  id: string;
  name: string;
  verified: boolean;
  professions: ProfessionEntry[];
  location: string;
  /** Absent at 0, which reads as "no experience" rather than "not known yet". */
  years?: number;
  photoUrl?: string;
  profileHref: string;
  payments: string[];
  specialties: string[];
  languages: string[];
  formats: string[];
  clientTypes: string[];
  aboutExcerpt?: string;
  footnote?: string;
  licenceValidUntil?: string;
};

const labels = (values: { label: string }[]) => values.map(({ label }) => label);

export const practitionerName = ({ title, firstName, lastName }: PractitionerCardSource) =>
  [title?.label, firstName, lastName].filter(Boolean).join(" ");

export const toProfessions = (
  credentials: { type: PractitionerType; psychologyEndorsements: TaxonomyOption[] }[],
): ProfessionEntry[] => {
  const byType = new Map<PractitionerType, Map<string, TaxonomyOption>>();
  for (const { type, psychologyEndorsements } of credentials) {
    const endorsements = byType.get(type) ?? new Map<string, TaxonomyOption>();
    for (const endorsement of psychologyEndorsements) endorsements.set(endorsement.slug, endorsement);
    byType.set(type, endorsements);
  }
  return [...byType.entries()].map(([type, endorsements]) => ({ type, endorsements: [...endorsements.values()] }));
};

const formatMonthYear = (iso?: string | null): string | undefined =>
  iso ? new Intl.DateTimeFormat("en-AU", { month: "long", year: "numeric" }).format(new Date(iso)) : undefined;

const toFootnote = (source: PractitionerCardSource): string | undefined => {
  const admin = isAdminSource(source);
  // Hidden for now: "Listed since" read as noise repeated across every card.
  // const listed = formatMonthYear(admin ? (source.listedAt ?? source.createdAt) : source.createdAt);
  const updated = admin ? formatMonthYear(source.updatedAt) : undefined;
  const parts = [
    // listed ? `Listed on CMW directory since ${listed}.` : undefined,
    updated ? `Profile last updated ${updated}.` : undefined,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : undefined;
};

export const toCardContent = (source: PractitionerCardSource, showDistance = true): CardContent => {
  const admin = isAdminSource(source);
  const practiceAddress = source.practiceAddresses[0];
  const distanceMetres = admin || !showDistance ? undefined : source.distanceMetres;

  return {
    id: admin ? source.id : source.username,
    name: practitionerName(source),
    verified: source.credentials.some(isCredentialVerified),
    professions: toProfessions(source.credentials),
    location: [
      suburbOf(practiceAddress),
      distanceMetres !== undefined ? formatDistance(distanceMetres) : undefined,
    ]
      .filter(Boolean)
      .join(", "),
    years: source.derivedYearsOfExperience > 0 ? source.derivedYearsOfExperience : undefined,
    photoUrl: source.profilePhotoUrl || undefined,
    profileHref: `/practitioner/${source.username}`,
    payments: labels(source.fundingPathways),
    specialties: labels(source.focusAreas),
    languages: labels(source.languages),
    formats: source.sessionFormats,
    clientTypes: labels(source.clientTypes),
    aboutExcerpt: deltaText(displayAbout(source)) || undefined,
    footnote: toFootnote(source),
    licenceValidUntil: admin ? source.licenceValidUntil : undefined,
  };
};
